import { log } from "@/core/logger";
import { createRoomAPI } from "../api/apiFactory";
import type { IRoomAPI } from "../api/roomApi";
import type { Room, RoomFilters, RoomSearchCriteria, RoomSort, RoomSuggestion } from "@/types/Room";

const floorOrder: Record<string, number> = {
  basement: 0,
  cellar: 0,
  ground: 1,
  mezzanine: 1.5,
  first: 2,
  second: 3,
  third: 4,
  fourth: 5,
  fifth: 6,
};

function getFloorValue(floor: string): number {
  return floorOrder[floor.toLowerCase()] ?? 99; // Unknown floors go to the end
}

/**
 * Service layer provides higher-level abstractions over the raw API.
 * Use this for:
 * - Client-side filtering/transformations
 * - Combining multiple API calls
 * - Business logic that doesn't belong in the API layer
 * - Caching strategies
 */
export class RoomService {
  public api: IRoomAPI;

  constructor(api?: IRoomAPI) {
    this.api = api || createRoomAPI();
  }

  private applyClientFilters(rooms: Room[], filters: RoomFilters): Room[] {
    return rooms.filter((room) => {
      if (filters.id && room.id !== filters.id) return false;
      if (filters.locationId && room.locationId !== filters.locationId) return false;
      if (filters.name && room.name !== filters.name) return false;
      if (filters.isAvailable !== undefined && room.available !== filters.isAvailable) return false;
      if (filters.isAccessible !== undefined && room.isAccessible !== filters.isAccessible) return false;
      if (filters.hasAmenities === true && (!room.amenities || room.amenities.length === 0)) return false;
      if (filters.hasTypeOfAmenityId) {
        if (!room.amenities || !room.amenities.some((amenity) => amenity.id === filters.hasTypeOfAmenityId)) {
          return false;
        }
      }
      if (filters.hasAllTypeOfAmenityIds) {
        if (
          !room.amenities ||
          !filters.hasAllTypeOfAmenityIds.every((id) => room.amenities!.some((amenity) => amenity.id === id))
        ) {
          return false;
        }
      }
      if (filters.hasGalleryImages === true && (!room.gallery || room.gallery.length === 0)) return false;
      if (filters.hasVideoTour === true && !room.videoTourUrl) return false;
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const nameMatch = room.name.toLowerCase().includes(searchLower);
        const descriptionMatch = room.description ? room.description.toLowerCase().includes(searchLower) : false;
        if (!nameMatch && !descriptionMatch) return false;
      }

      return true;
    });
  }

  private sortRooms(rooms: Room[], sort: RoomSort): Room[] {
    const sorted = [...rooms];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "floor":
          comparison = getFloorValue(a.floor || "") - getFloorValue(b.floor || "");
          break;
        case "available":
          comparison = a.available ? -1 : 1;
          break;
        case "displayOrder": {
          const orderA = a.displayOrder ?? 999999;
          const orderB = b.displayOrder ?? 999999;
          comparison = orderA - orderB;
          // Fallback to alphabetical if same order
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
        }
      }

      return sort.order === "asc" ? comparison : -comparison;
    });

    return sorted;
  }

  async getAllRoomsForLocation(locationId: string, filters?: RoomFilters, sort?: RoomSort): Promise<Array<Room>> {
    log("roomService", "getAllRoomsForLocation", `Fetching rooms for location: ${locationId}`);
    let rooms = await this.api.fetchAllRoomsForLocation(locationId);

    if (filters) {
      rooms = this.applyClientFilters(rooms, filters);
    }

    if (sort) {
      rooms = this.sortRooms(rooms, sort);
    }

    return rooms;
  }

  /**
   * Bulk fetch — returns every room in the system in a single request. Prefer this over a
   * per-location fan-out when you need room data for many locations at once (e.g. admin views).
   */
  async getAllRooms(filters?: RoomFilters, sort?: RoomSort): Promise<Array<Room>> {
    log("roomService", "getAllRooms", "Bulk-fetching all rooms");
    let rooms = await this.api.fetchAllRooms();

    if (filters) {
      rooms = this.applyClientFilters(rooms, filters);
    }

    if (sort) {
      rooms = this.sortRooms(rooms, sort);
    }

    return rooms;
  }

  async getRoomById(id: string): Promise<Room | null> {
    return this.api.fetchRoomById(id);
  }

  async findRooms(criteria: RoomSearchCriteria): Promise<RoomSuggestion[]> {
    const rooms = await this.api.findRooms(criteria);
    return rooms;
  }

  // this stops ts complaining about the unused private field
  async noop(): Promise<void> {
    console.log(this.api);
    return;
  }
}
